import { useState } from 'react'
import Editor from '@monaco-editor/react'
import Prose from './Prose'
import { QPARAM_THEME, registerLean4 } from './qparamMonaco'
import { gradeAnswer, type Artifact, type Question } from './grader'

/** Real FormalRV files, read-only, numbered as in the original file; fills its parent. */
export function ArtifactViewer({ artifacts }: { artifacts: Artifact[] }) {
  const [tab, setTab] = useState(0)
  const a = artifacts[tab]
  if (!a) return null
  const first = a.firstLine ?? 1
  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white rounded-lg border border-qcloud-border overflow-hidden">
      <div className="h-9 shrink-0 px-2 flex items-center gap-1 border-b border-qcloud-border bg-slate-50/80 overflow-x-auto">
        {artifacts.map((x, i) => (
          <button key={x.title} onClick={() => setTab(i)}
            className={`h-7 px-2.5 rounded-md text-xs font-mono whitespace-nowrap transition-colors ${
              i === tab ? 'bg-white border border-qcloud-border text-qcloud-primary shadow-sm' : 'text-qcloud-muted hover:text-qcloud-text'}`}>
            {x.title}
          </button>
        ))}
      </div>
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0">
          <Editor
            key={a.title}
            height="100%"
            language={a.language}
            theme={QPARAM_THEME}
            value={a.text}
            beforeMount={registerLean4}
            options={{
              readOnly: true, domReadOnly: true, minimap: { enabled: false }, fontSize: 13,
              lineNumbers: (n: number) => String(n + first - 1), lineNumbersMinChars: 5,
              wordWrap: 'on', scrollBeyondLastLine: false, automaticLayout: true, renderLineHighlight: 'none',
              folding: false, contextmenu: false,
            }}
          />
        </div>
      </div>
      <div className="h-8 shrink-0 px-3 flex items-center gap-3 border-t border-qcloud-border bg-slate-50 text-[11.5px] text-qcloud-muted overflow-x-auto whitespace-nowrap">
        <span className="font-mono text-qcloud-text">{a.path}</span>
        {a.firstLine && <span>from line {a.firstLine}</span>}
        <span>FormalRV {a.commit.slice(0, 8)}</span>
        {a.note && <span className="text-amber-700">{a.note}</span>}
      </div>
    </div>
  )
}

/** Questions answered from the artifacts; returns true to the caller once all are right. */
export function Questions({ questions, onAllCorrect }: { questions: Question[]; onAllCorrect: () => void }) {
  const [inputs, setInputs] = useState<string[]>(() => questions.map(() => ''))
  const [checked, setChecked] = useState<(boolean | null)[]>(() => questions.map(() => null))
  const [revealed, setRevealed] = useState(false)

  const check = () => {
    const r = questions.map((q, i) => gradeAnswer(q, inputs[i]))
    setChecked(r)
    if (r.every(Boolean)) onAllCorrect()
  }

  return (
    <div className="space-y-4">
      {questions.map((q, i) => {
        const ok = checked[i]
        return (
          <div key={i} className="space-y-1.5">
            <div className="flex gap-2 text-sm text-qcloud-text">
              <span aria-hidden className={`mt-0.5 w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
                ok === true ? 'bg-emerald-600 text-white' : ok === false ? 'bg-rose-600 text-white' : 'border-2 border-slate-300 text-slate-400'}`}>
                {ok === true ? '✓' : ok === false ? '✗' : i + 1}
              </span>
              <span><Prose text={q.q} /></span>
            </div>
            <div className="pl-7">
              {q.type === 'choice' ? (
                <div className="space-y-1">
                  {q.choices!.map((c, k) => (
                    <label key={k} className="flex items-start gap-2 text-sm text-qcloud-text cursor-pointer">
                      <input type="radio" name={`q${i}`} checked={inputs[i] === String(k)} className="mt-1"
                        onChange={() => setInputs(v => v.map((x, j) => (j === i ? String(k) : x)))} />
                      <span><Prose text={c} /></span>
                    </label>
                  ))}
                </div>
              ) : (
                <input value={inputs[i]} onChange={e => setInputs(v => v.map((x, j) => (j === i ? e.target.value : x)))}
                  onKeyDown={e => { if (e.key === 'Enter') check() }}
                  placeholder={q.type === 'number' ? 'a number' : 'your answer'}
                  className={`w-full max-w-sm px-2.5 py-1.5 rounded-lg border text-sm font-mono ${
                    ok === false ? 'border-rose-300' : ok ? 'border-emerald-300' : 'border-qcloud-border'}`} />
              )}
              {(ok || revealed) && (
                <p className="text-xs text-qcloud-muted mt-1.5">
                  {revealed && !ok && <><span className="font-semibold text-qcloud-text">Answer: </span>
                    {q.type === 'choice'
                      ? <Prose text={q.choices![Number(q.answer)]} className="text-qcloud-text" />
                      : <span className="font-mono text-qcloud-text">{String(q.answer)}</span>}. </>}
                  <Prose text={q.explain} />
                </p>
              )}
            </div>
          </div>
        )
      })}
      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={check} className="px-3 py-1.5 rounded-lg bg-qcloud-primary text-white text-sm font-semibold hover:opacity-90">Check answers</button>
        <button onClick={() => setRevealed(!revealed)} className="px-3 py-1.5 rounded-lg border border-qcloud-border text-sm hover:border-qcloud-primary">
          {revealed ? 'Hide answers' : 'Reveal answers'}
        </button>
      </div>
    </div>
  )
}
